import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, PlayCircle, Send, CheckCircle2, AlertOctagon, Clock, ChevronLeft, RefreshCw,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * EscrowDetail (V3.17)
 * ────────────────────
 * Vue partagée par les 3 rôles :
 *   - Client : peut valider ou disputer si UNDER_REVIEW / DELIVERED.
 *   - Talent : peut démarrer (HELD_IN_ESCROW), livrer (IN_PROGRESS).
 *   - Admin  : voit tout + boutons vérifier/résoudre.
 */
export default function EscrowDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [esc, setEsc] = useState(null);
  const [events, setEvents] = useState([]);
  const [yourRole, setYourRole] = useState("");
  const [loadError, setLoadError] = useState("");
  const [disputeReason, setDisputeReason] = useState("");
  const [showDispute, setShowDispute] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [resolveAction, setResolveAction] = useState("release");
  const [resolveSplit, setResolveSplit] = useState(50);
  const [resolveNote, setResolveNote] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const { data } = await api.get(`/v1/escrow/${id}`);
      setEsc(data.escrow);
      setEvents(data.events || []);
      setYourRole(data.your_role);
    } catch (err) {
      const msg = formatApiError(err);
      setLoadError(msg);
      toast.error(msg);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const countdown = useMemo(() => {
    if (!esc?.auto_release_deadline || !["DELIVERED", "UNDER_REVIEW"].includes(esc.status)) return null;
    const target = new Date(esc.auto_release_deadline).getTime();
    const now = Date.now();
    const diff = target - now;
    if (diff <= 0) return "Échéance atteinte";
    const days = Math.floor(diff / 86_400_000);
    const hours = Math.floor((diff % 86_400_000) / 3_600_000);
    return `${days} j ${hours} h`;
  }, [esc]);

  const call = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const actStart = () => call(async () => {
    await api.post(`/v1/escrow/${id}/start`);
    toast.success("Mission démarrée");
  });

  const actDeliver = () => call(async () => {
    if (!window.confirm("Confirmer la livraison du projet ?")) return;
    await api.post(`/v1/escrow/${id}/deliver`);
    toast.success("Livraison confirmée — le client est notifié");
  });

  const actValidate = () => call(async () => {
    if (!window.confirm("Valider et libérer le paiement au talent ?")) return;
    await api.post(`/v1/escrow/${id}/validate`);
    toast.success("Paiement libéré au talent");
  });

  const actDispute = () => call(async () => {
    if (!disputeReason.trim()) return toast.error("Motif obligatoire");
    await api.post(`/v1/escrow/${id}/dispute`, { reason: disputeReason });
    setShowDispute(false);
    setDisputeReason("");
    toast.success("Litige ouvert — AFRICTAL prend le relais");
  });

  const actVerify = (approved) => call(async () => {
    const note = approved ? undefined : window.prompt("Motif du refus :");
    if (!approved && !note) return;
    setVerifying(true);
    await api.post(`/v1/escrow/${id}/verify`, { approved, note });
    setVerifying(false);
    toast.success(approved ? "Fonds confirmés reçus" : "Preuve refusée");
  });

  const actResolve = () => call(async () => {
    if (!window.confirm(`Résoudre le litige avec l'action « ${resolveAction} » ?`)) return;
    const payload = { action: resolveAction, note: resolveNote || undefined };
    if (resolveAction === "split") payload.split_pct_to_talent = resolveSplit;
    await api.post(`/v1/escrow/${id}/resolve`, payload);
    setShowResolve(false);
    toast.success("Litige clôturé");
  });

  if (!esc) {
    if (loadError) {
      return (
        <PrivateLayout title="Séquestre">
          <div className="afr-empty" data-testid="escrow-detail-error">
            <div className="afr-empty__title">Impossible de charger ce séquestre</div>
            <div style={{ marginTop: 6, color: "#64748b" }}>{loadError}</div>
            <Link to="/escrow" className="afr-btn afr-btn--outline afr-btn--sm" style={{ marginTop: 12 }}>
              <ChevronLeft size={12} /> Retour à la liste
            </Link>
          </div>
        </PrivateLayout>
      );
    }
    return <PrivateLayout title="Séquestre"><div data-testid="escrow-detail-loading">Chargement…</div></PrivateLayout>;
  }

  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const isCompany = yourRole === "company";
  const isTalent = yourRole === "talent";

  return (
    <PrivateLayout
      title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        <Shield size={20} /> Séquestre #{esc.id.slice(0, 8)}
      </span>}
      breadcrumbs={[{ to: isAdmin ? "/admin/escrow" : "/escrow", label: "Séquestres" }, { label: esc.id.slice(0, 8) }]}
      actions={
        <div style={{ display: "flex", gap: 6 }}>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={load} data-testid="escrow-detail-refresh">
            <RefreshCw size={12} /> Actualiser
          </button>
          <Link to={isAdmin ? "/admin/escrow" : "/escrow"} className="afr-btn afr-btn--outline afr-btn--sm">
            <ChevronLeft size={14} /> Retour
          </Link>
        </div>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }} data-testid="escrow-detail-page">
        {/* Left */}
        <div>
          <StatusBanner esc={esc} yourRole={yourRole} countdown={countdown} />

          <section className="afr-card" style={{ padding: 14, marginBottom: 14 }} data-testid="escrow-summary-card">
            <h3 style={{ margin: 0, fontSize: "1rem" }}>Détails</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginTop: 10 }}>
              <Kv label="Montant" value={`${esc.amount.toLocaleString()} ${esc.currency}`} testid="escrow-detail-amount" />
              <Kv label="Commission" value={`${esc.commission_amount.toLocaleString()} ${esc.currency}`} />
              <Kv label="Net talent" value={`${esc.net_amount.toLocaleString()} ${esc.currency}`} testid="escrow-detail-net" />
              <Kv label="Plan" value={esc.deposit_plan === "full" ? "100 %" : "50 % + 50 %"} />
              <Kv label="Statut" value={esc.status} testid="escrow-detail-status" />
            </div>
            {esc.title && <div style={{ marginTop: 12 }}><strong>Titre :</strong> {esc.title}</div>}
            {esc.description && <div style={{ marginTop: 6, color: "#64748b" }}>{esc.description}</div>}
          </section>

          {/* Installments */}
          <section className="afr-card" style={{ padding: 14, marginBottom: 14 }} data-testid="escrow-installments-card">
            <h3 style={{ margin: 0, fontSize: "1rem" }}>Versements</h3>
            {(esc.installments || []).map((inst, idx) => (
              <div key={idx} style={{
                marginTop: 10, padding: 10, border: "1px solid #E2E8F0", borderRadius: 6,
              }} data-testid={`escrow-installment-${inst.n}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <strong>Versement #{inst.n}</strong> · {inst.expected.toLocaleString()} {esc.currency}
                  </div>
                  <span style={{
                    padding: "2px 8px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700,
                    background: badgeBg(inst.status), color: badgeFg(inst.status),
                  }}>{inst.status}</span>
                </div>
                {inst.declared_at && <div style={{ fontSize: "0.78rem", color: "#64748b", marginTop: 4 }}>Déclaré {formatDate(inst.declared_at)} · Canal : {inst.channel || "—"}</div>}
                {inst.proof_url && (
                  <div style={{ marginTop: 4 }}>
                    <a href={inst.proof_url} target="_blank" rel="noopener noreferrer"
                       data-testid={`escrow-installment-proof-${inst.n}`}
                       style={{ fontSize: "0.82rem", color: "#009640" }}>
                      Voir la preuve
                    </a>
                  </div>
                )}
                {isCompany && (inst.status === "pending" || inst.status === "rejected") && esc.status !== "COMPLETED" && (
                  <button
                    className="afr-btn afr-btn--primary afr-btn--sm"
                    onClick={() => navigate(`/escrow/${esc.id}/deposit`)}
                    style={{ marginTop: 8 }}
                    data-testid={`escrow-deposit-cta-${inst.n}`}
                  >
                    <Send size={12} /> Déposer les fonds
                  </button>
                )}
                {isAdmin && inst.status === "declared" && (
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={() => actVerify(true)}
                            disabled={busy || verifying} data-testid={`escrow-verify-approve-${inst.n}`}>
                      <CheckCircle2 size={12} /> Confirmer réception
                    </button>
                    <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => actVerify(false)}
                            disabled={busy || verifying} data-testid={`escrow-verify-reject-${inst.n}`}>
                      Refuser
                    </button>
                  </div>
                )}
              </div>
            ))}
          </section>

          {/* Actions contextuelles */}
          <section className="afr-card" style={{ padding: 14 }} data-testid="escrow-actions-card">
            <h3 style={{ margin: 0, fontSize: "1rem" }}>Actions</h3>
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {isTalent && esc.status === "HELD_IN_ESCROW" && (
                <button className="afr-btn afr-btn--primary" onClick={actStart} disabled={busy}
                        data-testid="escrow-start-btn">
                  <PlayCircle size={14} /> Commencer la mission
                </button>
              )}
              {isTalent && esc.status === "IN_PROGRESS" && (
                <button className="afr-btn afr-btn--primary" onClick={actDeliver} disabled={busy}
                        data-testid="escrow-deliver-btn">
                  <Send size={14} /> Confirmer la livraison du projet
                </button>
              )}
              {isCompany && ["DELIVERED", "UNDER_REVIEW"].includes(esc.status) && (
                <>
                  <button className="afr-btn afr-btn--primary" onClick={actValidate} disabled={busy}
                          data-testid="escrow-validate-btn">
                    <CheckCircle2 size={14} /> Valider et libérer
                  </button>
                  <button className="afr-btn afr-btn--outline" onClick={() => setShowDispute((s) => !s)}
                          data-testid="escrow-open-dispute-btn">
                    <AlertOctagon size={14} /> Signaler un problème
                  </button>
                </>
              )}
              {isAdmin && esc.status === "DISPUTED" && (
                <button className="afr-btn afr-btn--primary" onClick={() => setShowResolve((s) => !s)}
                        data-testid="escrow-open-resolve-btn">
                  <AlertOctagon size={14} /> Arbitrer le litige
                </button>
              )}
              {esc.status === "COMPLETED" && (
                <div data-testid="escrow-completed-note" style={{ color: "#065F46" }}>
                  <CheckCircle2 size={14} style={{ verticalAlign: "middle" }} /> Transaction clôturée
                </div>
              )}
            </div>

            {showDispute && (
              <div style={{ marginTop: 12, padding: 10, background: "#FEF3C7", borderRadius: 6 }}
                   data-testid="escrow-dispute-panel">
                <textarea
                  value={disputeReason}
                  onChange={(e) => setDisputeReason(e.target.value)}
                  placeholder="Décrivez le problème rencontré…"
                  rows={3}
                  data-testid="escrow-dispute-reason"
                  style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #E2E8F0", resize: "vertical" }}
                />
                <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: "flex-end" }}>
                  <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => setShowDispute(false)}>Annuler</button>
                  <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={actDispute} disabled={busy}
                          data-testid="escrow-dispute-submit">Ouvrir le litige</button>
                </div>
              </div>
            )}

            {showResolve && (
              <div style={{ marginTop: 12, padding: 10, background: "#FEE2E2", borderRadius: 6 }}
                   data-testid="escrow-resolve-panel">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                  {["release", "refund", "split"].map((a) => (
                    <button
                      key={a}
                      className={`afr-btn afr-btn--sm ${resolveAction === a ? "afr-btn--primary" : "afr-btn--outline"}`}
                      onClick={() => setResolveAction(a)}
                      data-testid={`escrow-resolve-action-${a}`}
                    >
                      {a === "release" ? "Libérer au talent" : a === "refund" ? "Rembourser client" : "Partager"}
                    </button>
                  ))}
                </div>
                {resolveAction === "split" && (
                  <div style={{ marginBottom: 8 }}>
                    <label style={{ fontSize: "0.85rem" }}>Part au talent : {resolveSplit} %</label>
                    <input type="range" min="0" max="100" step="5" value={resolveSplit}
                           onChange={(e) => setResolveSplit(Number(e.target.value))}
                           data-testid="escrow-resolve-split-slider"
                           style={{ width: "100%" }} />
                  </div>
                )}
                <textarea value={resolveNote} onChange={(e) => setResolveNote(e.target.value)}
                          placeholder="Note d'arbitrage (optionnel)…" rows={2}
                          data-testid="escrow-resolve-note"
                          style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #E2E8F0" }} />
                <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end" }}>
                  <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => setShowResolve(false)}>Annuler</button>
                  <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={actResolve} disabled={busy}
                          data-testid="escrow-resolve-submit">Confirmer l'arbitrage</button>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Right : Timeline */}
        <div>
          <section className="afr-card" style={{ padding: 14 }} data-testid="escrow-timeline-card">
            <h3 style={{ margin: 0, fontSize: "1rem" }}><Clock size={14} style={{ verticalAlign: "middle" }} /> Chronologie</h3>
            <ul style={{ listStyle: "none", padding: 0, margin: "10px 0 0" }}>
              {events.map((ev) => (
                <li key={ev.id} data-testid={`escrow-event-${ev.action}`}
                    style={{ padding: "6px 0", borderBottom: "1px dashed #E2E8F0", fontSize: "0.82rem" }}>
                  <div><strong>{eventLabel(ev.action)}</strong></div>
                  <div style={{ color: "#94a3b8", fontSize: "0.72rem" }}>
                    {formatDate(ev.at)} · {ev.actor_role || "system"}
                  </div>
                  {ev.note && <div style={{ color: "#64748b", fontStyle: "italic", fontSize: "0.78rem" }}>{ev.note}</div>}
                </li>
              ))}
              {events.length === 0 && <li style={{ color: "#94a3b8", fontSize: "0.85rem" }}>Aucun événement</li>}
            </ul>
          </section>
        </div>
      </div>
    </PrivateLayout>
  );
}

function StatusBanner({ esc, yourRole, countdown }) {
  const s = esc.status;
  const isTalent = yourRole === "talent";
  const isCompany = yourRole === "company";
  if (isTalent && s === "PENDING_PAYMENT") {
    return (
      <div className="afr-card" data-testid="escrow-status-banner-pending-talent"
           style={{ padding: 14, background: "#FEF3C7", color: "#78350F", marginBottom: 14, borderLeft: "4px solid #B45309" }}>
        <strong>En attente du dépôt du client.</strong> Ne commencez pas à travailler tant que les fonds ne sont pas sécurisés.
      </div>
    );
  }
  if (isTalent && (s === "HELD_IN_ESCROW" || s === "IN_PROGRESS")) {
    return (
      <div className="afr-card" data-testid="escrow-status-banner-secured"
           style={{ padding: 14, background: "linear-gradient(135deg,#065F46,#009640)", color: "#fff", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Shield size={28} />
          <div>
            <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>ARGENT SÉCURISÉ PAR AFRICTAL</div>
            <div style={{ fontSize: "0.88rem", marginTop: 2 }}>
              {esc.amount.toLocaleString()} {esc.currency} bloqués. Vous pouvez commencer le travail en toute sécurité.
              Votre paiement est garanti après validation de la mission.
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (s === "UNDER_REVIEW" && countdown) {
    return (
      <div className="afr-card" data-testid="escrow-status-banner-review"
           style={{ padding: 14, background: "#DBEAFE", color: "#1E40AF", marginBottom: 14, borderLeft: "4px solid #1E40AF" }}>
        <strong>En attente de validation client.</strong> {isCompany
          ? `Vous avez ${countdown} pour valider ou signaler un problème. Sans réponse, le paiement sera libéré automatiquement.`
          : `Le client dispose de ${countdown} pour valider ou signaler un problème.`}
      </div>
    );
  }
  if (s === "DISPUTED") {
    return (
      <div className="afr-card" data-testid="escrow-status-banner-disputed"
           style={{ padding: 14, background: "#FEE2E2", color: "#991B1B", marginBottom: 14, borderLeft: "4px solid #991B1B" }}>
        <strong>Litige ouvert.</strong> AFRICTAL arbitre. Les fonds restent gelés jusqu'à décision.
      </div>
    );
  }
  if (s === "COMPLETED") {
    return (
      <div className="afr-card" data-testid="escrow-status-banner-completed"
           style={{ padding: 14, background: "#DCFCE7", color: "#065F46", marginBottom: 14, borderLeft: "4px solid #009640" }}>
        <strong>Transaction clôturée.</strong> Le paiement a été libéré et l'historique est figé.
      </div>
    );
  }
  return null;
}

function Kv({ label, value, testid }) {
  return (
    <div>
      <div style={{ fontSize: "0.72rem", color: "#94a3b8", textTransform: "uppercase" }}>{label}</div>
      <div data-testid={testid} style={{ fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function badgeBg(s) {
  return ({
    pending: "#E2E8F0", declared: "#FEF3C7", verified: "#DCFCE7", rejected: "#FEE2E2",
  })[s] || "#E2E8F0";
}

function badgeFg(s) {
  return ({
    pending: "#475569", declared: "#78350F", verified: "#065F46", rejected: "#991B1B",
  })[s] || "#475569";
}

function eventLabel(action) {
  return ({
    created: "Séquestre créé",
    payment_declared: "Dépôt déclaré",
    payment_verified: "Dépôt vérifié",
    payment_rejected: "Dépôt refusé",
    mission_started: "Mission démarrée",
    delivered: "Livraison confirmée",
    disputed: "Litige ouvert",
    dispute_resolved: "Litige arbitré",
    released: "Paiement libéré",
    completed: "Mission clôturée",
  })[action] || action;
}

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
